/**
 * Size calculator.
 *
 * Recommendation rules come from size_recommendation metaobjects, rendered
 * into the markup as data-rules by blocks/size-calculator.liquid. Rules are
 * already filtered to the product's chart and sorted; the first match wins,
 * which is how the previous hardcoded if/else chain behaved.
 */

/**
 * Reads a message from the calculator's data attributes so the wording stays
 * editable in the theme editor. Falls back to the Finnish default when the
 * attribute is missing.
 * @param {string} name - data attribute name, e.g. "resultPrefix"
 * @param {string} fallback
 * @returns {string}
 */
function sizeCalculatorMessage(name, fallback) {
  const el = document.querySelector("[data-size-calculator]");
  const value = el && el.dataset[name];
  return value || fallback;
}

/**
 * @typedef {object} SizeRule
 * @property {string} size   - label shown to the customer, e.g. "M-L"
 * @property {number} hMin   - minimum height, always inclusive
 * @property {number} hMax   - maximum height
 * @property {boolean} hMaxIn - whether hMax belongs to this rule
 * @property {number} wMin   - minimum weight
 * @property {boolean} wMinEx - whether wMin is excluded from this rule
 * @property {number} wMax   - maximum weight, always inclusive
 */

/**
 * @returns {SizeRule[]} rules configured in the admin, empty if none
 */
function sizeCalculatorRules() {
  const el = document.querySelector("[data-size-calculator]");
  const raw = el && el.dataset.rules;
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * Finds the first rule covering this height and weight.
 * @param {SizeRule[]} rules
 * @param {number} height - cm
 * @param {number} weight - kg
 * @returns {string|null} size label, or null when nothing covers the pair
 */
function findSize(rules, height, weight) {
  for (const rule of rules) {
    const heightOk =
      height >= rule.hMin && (rule.hMaxIn ? height <= rule.hMax : height < rule.hMax);
    const weightOk =
      (rule.wMinEx ? weight > rule.wMin : weight >= rule.wMin) && weight <= rule.wMax;
    if (heightOk && weightOk) return rule.size;
  }
  return null;
}

/**
 * Reads the form, resolves a size and writes the result into the page.
 */
function calculateSize() {
  const height = parseFloat(
    /** @type {HTMLInputElement} */ (document.getElementById("length"))?.value
  );
  const weight = parseFloat(
    /** @type {HTMLInputElement} */ (document.getElementById("weight"))?.value
  );

  const errorContainer = document.querySelector(".product-size-guide__error");
  const errorText = document.getElementById("productSizeGuideErrorMessage");
  const resultElement = document.getElementById("productSizeGuideResult");
  const resultContainer = document.querySelector(".product-size-guide__result");

  if (isNaN(height) || isNaN(weight)) {
    if (errorText) {
      errorText.innerText = sizeCalculatorMessage("errorMessage", "Tarkista pituus ja paino");
    }
    errorContainer?.classList.add("open");
    // Hide any earlier result, otherwise a stale recommendation sits next to
    // the error and reads as if it still applied.
    if (resultElement) resultElement.innerText = "";
    resultContainer?.classList.remove("open");
    return;
  }

  errorContainer?.classList.remove("open");
  if (errorText) errorText.innerText = "";

  const size = findSize(sizeCalculatorRules(), height, weight);
  if (resultElement) {
    resultElement.innerText = size
      ? `${sizeCalculatorMessage("resultPrefix", "Suosittelemme kokoa:")} ${size}`
      : sizeCalculatorMessage("noSizeMessage", "Kokoa ei saatavilla");
  }
  resultContainer?.classList.add("open");
}

// The markup previously called one of two chart-specific functions. Both names
// stay available so a cached page or an older block still works. They are
// attached to window because inline onclick handlers resolve against globals,
// and a top-level const in a classic script would not be reachable there.
window.calculateSize = calculateSize;
window.calculateSizeNumbers = calculateSize;
window.calculateSizeLetters = calculateSize;
